import {
    CHAT_V2_PROGRESS_SCHEMA_VERSION,
    normalizeChatV2Progress,
} from './chatV2ProgressUtils';

const phase = (id, label, message) => Object.freeze({ id, label, message });
const WAITING = phase('waiting', 'Thinking', 'I’m getting started on your question.');
const UNDERSTANDING = phase('understanding', 'Thinking', 'I’m reading your question and getting a sense of what would be helpful.');
const SEARCH_MESSAGE = 'I’m looking through podcast conversations for ideas and perspectives that fit your question.';
const RESPONDING = phase('responding', 'Putting your response together', 'I’m keeping your response focused on what you asked.');
const CLARIFYING = phase('responding', 'Preparing a quick question', 'I’ll ask a quick question to understand what you’re looking for.');
const SUPPORTIVE = phase('responding', 'Preparing your response', 'I’m taking care with your response and focusing on what might help right now.');

const SEARCH_LABELS = new Map([
    ['searching short podcast highlights', 'Searching podcast highlights'],
    ['searching episodes', 'Searching episodes'],
    ['searching shows', 'Searching shows'],
    ['searching speakers', 'Searching speakers'],
]);

const getActiveStep = (progress) => {
    if (
        progress?.schemaVersion !== CHAT_V2_PROGRESS_SCHEMA_VERSION ||
        progress.state !== 'running'
    ) return null;

    if (progress.currentStepId) {
        const current = progress.steps.find((step) => step.id === progress.currentStepId);
        return current?.state === 'running' ? current : null;
    }

    const runningSteps = progress.steps.filter((step) => step.state === 'running');
    return runningSteps.length === 1 ? runningSteps[0] : null;
};

// Use only the server's explicit public sentence, never internal plan details.
// Older servers and unfamiliar progress shapes retain a conversational fallback.
export const getChatLoadingPhase = (value, { useServerPlanningMessages = false } = {}) => {
    const progress = normalizeChatV2Progress(value);
    const current = getActiveStep(progress);

    if (!current) return WAITING;
    if (current.id === 'understanding') {
        return useServerPlanningMessages && progress.message
            ? { ...UNDERSTANDING, message: progress.message }
            : UNDERSTANDING;
    }
    if (current.id === 'searching' || current.id === 'reviewing') {
        if (progress.steps.some((step) => step.state === 'skipped')) return WAITING;
        const title = current.title.toLowerCase();
        const label = current.id === 'searching'
            ? SEARCH_LABELS.get(title) || 'Searching podcasts'
            : title === 'choosing recommendations' ? 'Choosing recommendations' : 'Putting your response together';
        return {
            ...phase(current.id, label, progress.userMessage || SEARCH_MESSAGE),
            ...(current.id === 'searching' && progress.activities?.length ? { activities: progress.activities } : {}),
            ...(current.id === 'reviewing' && (progress.sources?.speakers.length || progress.sources?.podcasts.length
                || progress.sources?.speakerCount > 0 || progress.sources?.podcastCount > 0)
                ? { sources: progress.sources } : {}),
        };
    }
    if (current.id !== 'responding') return WAITING;

    const title = current.title.toLowerCase();
    if (title === 'preparing a supportive response') return SUPPORTIVE;
    if (title === 'preparing one quick question' || title === 'one quick question') return CLARIFYING;
    return RESPONDING;
};
